import { useTranslation } from "react-i18next";
import { CalendarClock, Trash2 } from "lucide-react";
import { useAppDispatch } from "../../../app/hooks";
import { ImportSession } from "../models/ImportSession";
import { removeSession, updateSession } from "../assistant-import-slice";

const WEEKDAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

interface ProposalSessionsSectionProps {
  sessions: ImportSession[];
  memberNamesByTempId: Map<string, string>;
  groupNamesByTempId: Map<string, string>;
}

export function ProposalSessionsSection({
  sessions,
  memberNamesByTempId,
  groupNamesByTempId,
}: ProposalSessionsSectionProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  if (sessions.length === 0) return null;

  const toggleWeekday = (session: ImportSession, day: string) => {
    const weekdays = session.weekdays.includes(day)
      ? session.weekdays.filter((d) => d !== day)
      : [...session.weekdays, day];
    dispatch(updateSession({ tempId: session.tempId, changes: { weekdays } }));
  };

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-95 text-primary-50 dark:bg-primary-30 dark:text-primary-80">
          <CalendarClock size={20} strokeWidth={2} />
        </span>
        <h2 className="text-lg font-semibold">
          {t("assistantImport.review.sessions")} ({sessions.length})
        </h2>
      </div>

      <div className="space-y-3">
        {sessions.map((session) => {
          const attendees = [
            ...session.groupTempIds.map((id) => groupNamesByTempId.get(id) ?? id),
            ...session.memberTempIds.map((id) => memberNamesByTempId.get(id) ?? id),
          ];
          return (
            <div key={session.tempId} className="rounded-xl bg-grey-95 dark:bg-primary-20 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <input
                  className="input flex-1 min-w-[180px]"
                  aria-label={t("assistantImport.review.sessionTitle")}
                  value={session.title}
                  onChange={(e) =>
                    dispatch(updateSession({ tempId: session.tempId, changes: { title: e.target.value } }))
                  }
                />
                <input
                  className="input w-28"
                  aria-label={t("assistantImport.review.time")}
                  type="time"
                  value={session.timeLocal}
                  onChange={(e) =>
                    dispatch(updateSession({ tempId: session.tempId, changes: { timeLocal: e.target.value } }))
                  }
                />
                <label className="flex items-center gap-1 text-sm text-grey-50 dark:text-grey-70">
                  <input
                    className="input w-20"
                    aria-label={t("assistantImport.review.duration")}
                    type="number"
                    min={5}
                    max={1440}
                    value={session.durationMinutes}
                    onChange={(e) =>
                      dispatch(
                        updateSession({
                          tempId: session.tempId,
                          changes: { durationMinutes: Number(e.target.value) },
                        }),
                      )
                    }
                  />
                  {t("assistantImport.review.minutes")}
                </label>
                <button
                  type="button"
                  className="btn btn-text text-red-700 dark:text-red-400"
                  aria-label={t("common.remove")}
                  onClick={() => dispatch(removeSession(session.tempId))}
                >
                  <Trash2 size={18} />
                </button>
              </div>

              <div className="mt-2 flex flex-wrap gap-1.5">
                {WEEKDAYS.map((day) => {
                  const active = session.weekdays.includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      aria-pressed={active}
                      className={
                        active
                          ? "rounded-full bg-primary-40 px-3 py-1 text-sm text-white dark:bg-primary-80 dark:text-primary-10"
                          : "rounded-full bg-white px-3 py-1 text-sm text-grey-30 dark:bg-primary-10 dark:text-grey-70"
                      }
                      onClick={() => toggleWeekday(session, day)}
                    >
                      {t(`assistantImport.weekdays.${day}`)}
                    </button>
                  );
                })}
              </div>
              {session.weekdays.length === 0 && (
                <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                  {t("assistantImport.review.weekdayRequired")}
                </p>
              )}

              <div className="mt-2 flex flex-wrap gap-1.5">
                {attendees.map((name, index) => (
                  <span
                    key={`${name}-${index}`}
                    className="rounded-full bg-primary-90 dark:bg-primary-30 px-2 py-0.5 text-sm text-primary-30 dark:text-primary-90"
                  >
                    {name}
                  </span>
                ))}
                {attendees.length === 0 && (
                  <span className="text-sm text-red-700 dark:text-red-400">
                    {t("assistantImport.review.attendeeRequired")}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
